import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaPhoneAlt, FaChevronDown, FaBars, FaTimes } from 'react-icons/fa';
import Classes from './Header.module.css';
import HomeLink from '../HomeLink/HomeLink';
import { SITE } from '../../../data/siteConfig';
import { SERVICES } from '../../../data/services';
import { AREA_GROUPS } from '../../../data/serviceAreas';


// White-topped pages where the header needs its shadow from the start.
const SHADOW_PATHS = ['/privacy-policy', '/terms-of-service', '/contact-us'];

export default function Header() {
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    // Hides the hover dropdowns after a link in them is clicked, until the
    // cursor leaves the nav item (otherwise :hover keeps them open).
    const [menuHidden, setMenuHidden] = useState(false);

    const closeDropdown = () => {
        setMenuHidden(true);
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    };
    const dropdownProps = {
        className: `${Classes.hasMenu} ${menuHidden ? Classes.menuHidden : ''}`,
        onMouseLeave: () => setMenuHidden(false),
        onClick: (e) => { if (e.target.closest('a')) closeDropdown(); },
    };

    // Close the mobile menu on navigation.
    useEffect(() => { setMenuOpen(false); }, [location.pathname, location.hash]);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = menuOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [menuOpen]);

    return (
        <>
            {/* Main header */}
            <header className={`${Classes.header} ${scrolled || SHADOW_PATHS.includes(location.pathname) ? Classes.scrolled : ''}`}>
                <div className={Classes.headerInner}>
                    <HomeLink className={Classes.logo} aria-label={`${SITE.name} home`}>
                        <img src="/assets/images/logo-color.svg" alt={`${SITE.name} logo`} width="106" height="52" />
                    </HomeLink>

                    <nav className={Classes.nav} aria-label="Main">
                        <ul>
                            <li {...dropdownProps}>
                                <Link to="/#services" className={Classes.navLink}>Services <FaChevronDown /></Link>
                                <div className={`${Classes.menu} ${Classes.menuList}`}>
                                    {SERVICES.map((s) => (
                                        <Link key={s.slug} to={`/services/${s.slug}`} className={Classes.menuLink}>{s.name}</Link>
                                    ))}
                                </div>
                            </li>
                            <li {...dropdownProps}>
                                <Link to="/#service-areas" className={`${Classes.navLink} ${location.pathname.startsWith('/service-areas/') ? Classes.navActive : ''}`}>Service Areas <FaChevronDown /></Link>
                                <div className={`${Classes.menu} ${Classes.menuGroups}`}>
                                    {AREA_GROUPS.map((g) => (
                                        <div key={g.county}>
                                            <p className={Classes.menuHeading}>{g.county}</p>
                                            {g.areas.map((a) => (
                                                <Link key={a.slug} to={`/service-areas/${a.slug}`} className={Classes.menuLink}>{a.city}</Link>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            </li>
                            <li><Link to="/#why-us" className={Classes.navLink}>Why Choose Us</Link></li>
                            <li><Link to="/#faq" className={Classes.navLink}>FAQ</Link></li>
                        </ul>
                    </nav>

                    <div className={Classes.actions}>
                        <a href={`tel:${SITE.phoneE164}`} className={Classes.phone}>
                            <strong>{SITE.phone}</strong>
                        </a>
                        <a href={SITE.bookingUrl} target="_blank" rel="noopener noreferrer" className="btn btn-dark btn-sm">
                            Book Online
                        </a>
                        <button
                            type="button"
                            className={Classes.burger}
                            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                            aria-expanded={menuOpen}
                            onClick={() => setMenuOpen((o) => !o)}
                        >
                            {menuOpen ? <FaTimes /> : <FaBars />}
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile drawer */}
            <div className={`${Classes.drawer} ${menuOpen ? Classes.drawerOpen : ''}`} aria-hidden={!menuOpen}>
                <div className={Classes.drawerTop}>
                    <HomeLink className={Classes.logo} aria-label={`${SITE.name} home`} onClick={() => setMenuOpen(false)}>
                        <img src="/assets/images/logo-color.svg" alt={`${SITE.name} logo`} width="106" height="52" />
                    </HomeLink>
                    <button type="button" className={Classes.burger} aria-label="Close menu" onClick={() => setMenuOpen(false)}>
                        <FaTimes />
                    </button>
                </div>
                <div className={Classes.drawerBody}>
                    <p className={Classes.drawerLabel}>Services</p>
                    {SERVICES.map((s) => (
                        <Link key={s.slug} to={`/services/${s.slug}`}>{s.name}</Link>
                    ))}
                    {AREA_GROUPS.map((g) => (
                        <React.Fragment key={g.county}>
                            <p className={Classes.drawerLabel}>{g.county}</p>
                            <div className={Classes.drawerGrid}>
                                {g.areas.map((a) => (
                                    <Link key={a.slug} to={`/service-areas/${a.slug}`}>{a.city}</Link>
                                ))}
                            </div>
                        </React.Fragment>
                    ))}
                    <p className={Classes.drawerLabel}>Company</p>
                    <Link to="/#why-us">Why Choose Us</Link>
                    <Link to="/#faq">FAQ</Link>
                    <Link to="/contact-us">Contact Us</Link>
                </div>
                <div className={Classes.drawerCta}>
                    <a href={`tel:${SITE.phoneE164}`} className="btn btn-outline"><FaPhoneAlt /> {SITE.phone}</a>
                    <a href={SITE.bookingUrl} target="_blank" rel="noopener noreferrer" className="btn btn-dark">Book Online</a>
                </div>
            </div>
        </>
    );
}
